import Box from "@mui/material/Box";
import { isDarkColor } from "common/utilities/colors/colors";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import { nanoid } from "nanoid";
import { useMemo } from "react";
import isEqual from "react-fast-compare";
import { useSelector } from "react-redux";
import type { SequenceAlign, SequenceFeature } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { HandleResidueHover } from "sequenceAnalyser/components/Sequences/SequenceTable/SequenceTable";
import { SequenceTableRowResiduesChunk } from "sequenceAnalyser/components/Sequences/SequenceTableRowResiduesChunk/SequenceTableRowResiduesChunk";
import SequenceTableRowTitle from "sequenceAnalyser/components/Sequences/SequenceTableRowTitle/SequenceTableRowTitle";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import type { SelectedColorScheme } from "sequenceAnalyser/utilities/colors";
import { groupFeaturesByName } from "sequenceAnalyser/utilities/features/features";
import {
  RESIDUE_PIXEL_HEIGHT,
  RESIDUE_PIXEL_WIDTH,
  sequencePixelHeight,
} from "sequenceAnalyser/utilities/screen/screen";
import type { SequenceSearchMatch, SequencesSearch } from "sequenceAnalyser/utilities/search/search";
import type { FeatureDefinitions, SequenceAnalyserTableView } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import {
  buildFeatureId,
  hasAnyFeatureDefinitionVisible,
} from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

interface ComponentProps {
  rowId: number;
  name: string;
  sequence: SequenceAlign;
  qualifiedId: string;
  alignedSequences: SequenceAlign[];
  features: SequenceFeature[];
  featureDefinitions: FeatureDefinitions;
  onResidueHover: HandleResidueHover;
  colors?: SelectedColorScheme;
  hideTitle?: boolean;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  view: SequenceAnalyserTableView;
  onDragNDrop?: (from: number, to: number) => void;
  search: SequencesSearch;
  searchActiveMatch?: SequenceSearchMatch;
  url?: string;
  zoomLevel: number;
  expandedFeaturesForSequences: number[];
  oppositeStrand?: boolean;
  maskingResidue: string;
  windowInnerSize: string;
  alwaysVisible?: boolean;
}

function SequenceTableRow({
  rowId,
  name,
  sequence,
  features,
  featureDefinitions,
  onResidueHover,
  qualifiedId,
  colors,
  hideTitle,
  columnsLetterOccurrences,
  view,
  onDragNDrop,
  search,
  searchActiveMatch,
  alignedSequences,
  url,
  zoomLevel,
  expandedFeaturesForSequences,
  oppositeStrand,
  maskingResidue,
  windowInnerSize,
}: ComponentProps) {
  const visibleFeatures = features.filter((feature) => featureDefinitions[buildFeatureId(feature)].visible);
  const featuresGroups = groupFeaturesByName(visibleFeatures);
  const showExpandedFeatures = expandedFeaturesForSequences.includes(sequence.id);
  const hasFeatures = hasAnyFeatureDefinitionVisible(featureDefinitions);
  const hasColorsAndFeaturesLayout = colors !== undefined && !showExpandedFeatures;
  const x = useSelector((state: ApplicationState) => state.sequenceAnalyser.scrollPosition?.x ?? 0);
  const columnWidth = RESIDUE_PIXEL_WIDTH * (zoomLevel / 100);
  const width = useMemo(() => parseInt(windowInnerSize.split("*")[0]), [windowInnerSize]);
  const [start, stop] = useMemo(() => {
    const start = Math.floor(x / columnWidth);
    return [start, Math.ceil(width / columnWidth) + start];
  }, [columnWidth, x, width]);

  return (
    <>
      <div
        className="sequence"
        // Parent container of a scaled element need to have the height adjusted
        // otherwise its height would remain the same
        style={{
          height: `${sequencePixelHeight(colors !== undefined, hasFeatures && !showExpandedFeatures, zoomLevel)}px`,
        }}
      >
        {hideTitle !== true && (
          <SequenceTableRowTitle
            sequenceId={sequence.id}
            rowId={rowId}
            name={name}
            onDragNDrop={onDragNDrop}
            alignedSequences={alignedSequences}
            url={url}
            onResidueHover={onResidueHover}
            oppositeStrand={oppositeStrand}
          />
        )}
        <Box position="relative" display="inline-block" className="residuesWrapper zoomable">
          {/* View of the features in the background */}
          {!showExpandedFeatures &&
            visibleFeatures.map((feature) => (
              <div
                key={`${feature.id}-${nanoid()}`}
                className="feature"
                style={{
                  width: `${(feature.stop - feature.start + 1) * RESIDUE_PIXEL_WIDTH}px`,
                  left: `${feature.start * RESIDUE_PIXEL_WIDTH}px`,
                  backgroundColor: `rgb(${featureDefinitions[buildFeatureId(feature)].color.join(",")})`,
                }}
              />
            ))}
          {/* View of the search matches overlapping features */}
          {(search.matches[sequence.id] || []).map((match) => {
            const isSearchActiveMatch = match !== undefined && isEqual(match, searchActiveMatch);

            return (
              <div
                key={match.residueId}
                className={`searchMatch ${isSearchActiveMatch ? "active" : ""}`}
                style={{
                  width: `${(match.end + 1 - match.start) * RESIDUE_PIXEL_WIDTH - 1}px`,
                  left: `${match.start * RESIDUE_PIXEL_WIDTH}px`,
                }}
              />
            );
          })}
          <SequenceTableRowResiduesChunk
            rowId={rowId}
            sequenceId={sequence.id}
            sequenceQualifiedId={qualifiedId}
            sequenceName={name}
            sequenceResidues={sequence.residues}
            features={features}
            noFeaturesColors={showExpandedFeatures}
            featureDefinitions={featureDefinitions}
            onResidueHover={onResidueHover}
            colors={colors}
            columnsLetterOccurrences={columnsLetterOccurrences}
            view={view}
            hasColorsAndFeaturesLayout={hasColorsAndFeaturesLayout}
            maskingResidue={maskingResidue}
            start={start}
            stop={stop}
          />
        </Box>
      </div>

      {!!showExpandedFeatures &&
        Object.values(featuresGroups).map((fs) => {
          const featureName = fs[0].name ?? fs[0].type;
          const uniqueId = nanoid();
          const bgColor = featureDefinitions[buildFeatureId(fs[0])].color;
          return (
            <div
              key={`${featureName}-${uniqueId}`}
              style={{
                // Required for the features to be placed correctly when exporting to PNG
                position: "relative",
                width: "100%",
                height: `${sequencePixelHeight(false, false, zoomLevel)}px`,
                backgroundColor: "rgba(255, 255, 255, 0.75)",
              }}
              className="sequenceFeature"
              onMouseEnter={() => onResidueHover(undefined)}
            >
              <div
                className={[
                  "title zoomable", // styled in SequencesStyling.style
                  !isDarkColor(bgColor) ? "lightBg" : "darkBg",
                ].join(" ")}
                style={{
                  backgroundColor: `rgb(${bgColor.join(",")})`,
                }}
              >
                <span className="name">{featureName}</span>
              </div>
              <Box position="absolute" display="inline-block" className="featureWrapper zoomable">
                {fs.map((feature) => (
                  <div
                    key={feature.id}
                    className="feature"
                    style={{
                      position: "absolute",
                      width: `${(feature.stop - feature.start + 1) * RESIDUE_PIXEL_WIDTH}px`,
                      height: `${RESIDUE_PIXEL_HEIGHT - 1}px`,
                      marginBottom: "1px",
                      left: `${feature.start * RESIDUE_PIXEL_WIDTH}px`,
                      backgroundColor: `rgb(${featureDefinitions[buildFeatureId(feature)].color.join(",")})`,
                    }}
                  />
                ))}
              </Box>
            </div>
          );
        })}
    </>
  );
}

export default SequenceTableRow;
